import { SlbAuctionStatus } from '@/types/auction';

/**
 * User-friendly labels for auction statuses.
 * Used across the application for consistent display (filters, badges, forms, etc.)
 *
 * This is the single source of truth for all status labels.
 * The `as const` assertion makes this object deeply readonly and allows TypeScript
 * to infer the exact string literal types.
 */
export const AUCTION_STATUS_LABELS = {
    [SlbAuctionStatus.NEW]: 'Disabled',
    [SlbAuctionStatus.UNPUBLISHED]: 'Pending',
    [SlbAuctionStatus.PUBLISHED]: 'Online',
    [SlbAuctionStatus.PAUSED]: 'Paused',
    [SlbAuctionStatus.CLOSED]: 'Closed',
    [SlbAuctionStatus.DELETED]: 'Deleted',
} as const;

/**
 * Type for user-friendly status values used in forms.
 * Derived from AUCTION_STATUS_LABELS to ensure type safety and DRY.
 */
export type AuctionStatusFormValue =
    | (typeof AUCTION_STATUS_LABELS)[SlbAuctionStatus.NEW]
    | (typeof AUCTION_STATUS_LABELS)[SlbAuctionStatus.UNPUBLISHED]
    | (typeof AUCTION_STATUS_LABELS)[SlbAuctionStatus.PUBLISHED];

/**
 * Maps backend SlbAuctionStatus enum to user-friendly form value
 */
export const mapAuctionStatusToFormValue = (status: SlbAuctionStatus): AuctionStatusFormValue => {
    switch (status) {
        case SlbAuctionStatus.PUBLISHED:
            return AUCTION_STATUS_LABELS[SlbAuctionStatus.PUBLISHED];
        case SlbAuctionStatus.NEW:
            return AUCTION_STATUS_LABELS[SlbAuctionStatus.NEW];
        case SlbAuctionStatus.UNPUBLISHED:
            return AUCTION_STATUS_LABELS[SlbAuctionStatus.UNPUBLISHED];
        case SlbAuctionStatus.PAUSED:
        case SlbAuctionStatus.CLOSED:
        case SlbAuctionStatus.DELETED:
        default:
            return AUCTION_STATUS_LABELS[SlbAuctionStatus.NEW];
    }
};

/**
 * Maps user-friendly form value to backend SlbAuctionStatus enum
 */
export const mapFormValueToAuctionStatus = (formStatus: AuctionStatusFormValue): SlbAuctionStatus => {
    switch (formStatus) {
        case AUCTION_STATUS_LABELS[SlbAuctionStatus.PUBLISHED]:
            return SlbAuctionStatus.PUBLISHED;
        case AUCTION_STATUS_LABELS[SlbAuctionStatus.UNPUBLISHED]:
            return SlbAuctionStatus.UNPUBLISHED;
        case AUCTION_STATUS_LABELS[SlbAuctionStatus.NEW]:
        default:
            return SlbAuctionStatus.NEW;
    }
};
